import React, { useState } from 'react';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';

import api from '../../api';
import CopyButton from '../../components/CopyButton';
import AsyncSection from './AsyncSection';

const ALARM_TYPE_LABELS = { inverter: 'Inverter', battery: 'Battery' };

const alarmKey = alarm => `${alarm.alarm_type}:${alarm.alarm_code}`;

// Ranked alarm list where each row expands to the gateways affected by that alarm.
// Affected gateways are fetched lazily on first expand and cached per alarm.
const GatewayAlarms = ({ alarms, from, to }) => {
    const history = useHistory();
    const [openKey, setOpenKey] = useState(null);
    const [devicesByKey, setDevicesByKey] = useState({});
    const [loadingKey, setLoadingKey] = useState(null);
    const [errorKey, setErrorKey] = useState(null);

    // Open a gateway's page, passing the row through router state so it shows
    // name/serial/customer immediately (the same shape GatewayPage reads).
    const openGateway = device =>
        history.push({
            pathname: `/gateways/${device.device_id}`,
            state: {
                gateway: {
                    device_id: device.device_id,
                    name: device.name,
                    reference: device.reference,
                    retailer_customer_id: device.retailer_customer_id,
                    address: device.address,
                },
            },
        });

    const maxAlarmCount = alarms.length ? Math.max(...alarms.map(a => a.count)) : 0;
    const totalAlarmCount = alarms.reduce((sum, a) => sum + a.count, 0);

    const toggle = async alarm => {
        const key = alarmKey(alarm);
        if (openKey === key) {
            setOpenKey(null);
            return;
        }
        setOpenKey(key);
        if (devicesByKey[key]) {
            return;
        }
        setLoadingKey(key);
        setErrorKey(null);
        try {
            const res = await api.get_gateway_alarm_devices(alarm.alarm_type, alarm.alarm_code, from, to);
            setDevicesByKey(prev => ({ ...prev, [key]: res.data || [] }));
        } catch (err) {
            setErrorKey(key);
        } finally {
            setLoadingKey(null);
        }
    };

    if (alarms.length === 0) {
        return <div className="gateways-overview__message">No alarms reported in this period.</div>;
    }

    return (
        <ul className="gateways-overview__alarm-list">
            {alarms.map(alarm => {
                const key = alarmKey(alarm);
                const isOpen = openKey === key;
                const devices = devicesByKey[key] || [];
                const barWidth = maxAlarmCount > 0 ? Math.round((100 * alarm.count) / maxAlarmCount) : 0;
                const share = totalAlarmCount > 0 ? Math.round((100 * alarm.count) / totalAlarmCount) : 0;
                return (
                    <li key={key} className="gateways-overview__alarm-item">
                        <button
                            type="button"
                            className={`gateways-overview__alarm ${isOpen ? 'gateways-overview__alarm--open' : ''}`}
                            onClick={() => toggle(alarm)}
                            aria-expanded={isOpen}
                            title={`Seen in ${alarm.count} hourly gateway reports (${share}% of all alarms)`}
                        >
                            <span className={`gateways-overview__alarm-tag gateways-overview__alarm-tag--${alarm.alarm_type}`}>
                                {ALARM_TYPE_LABELS[alarm.alarm_type] || alarm.alarm_type}
                            </span>
                            <span className="gateways-overview__alarm-code">{alarm.alarm_code}</span>
                            <span className="gateways-overview__alarm-meter">
                                <span className="gateways-overview__alarm-bar" style={{ width: `${barWidth}%` }} />
                            </span>
                            <span className="gateways-overview__alarm-count">
                                {alarm.count}
                                <span className="gateways-overview__alarm-share">{share}%</span>
                            </span>
                            <span className="gateways-overview__alarm-caret">{isOpen ? '–' : '+'}</span>
                        </button>

                        {isOpen ? (
                            <div className="gateways-overview__alarm-devices">
                                <AsyncSection
                                    isReady={loadingKey !== key}
                                    error={errorKey === key}
                                    isEmpty={devices.length === 0}
                                    errorText="Could not load affected gateways."
                                    emptyText="No affected gateways found."
                                >
                                    <table className="gateways-overview__alarm-devices-table">
                                        <thead>
                                            <tr>
                                                <th>Gateway</th>
                                                <th>Device ID</th>
                                                <th>Serial</th>
                                                <th>Customer</th>
                                                <th>Occurrences</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {devices.map(device => (
                                                <tr
                                                    key={device.device_id}
                                                    className="gateways-overview__alarm-device-row"
                                                    onClick={() => openGateway(device)}
                                                >
                                                    <td>{device.name || '—'}</td>
                                                    <td className="gateways-overview__mono" onClick={e => e.stopPropagation()}>
                                                        {device.device_id}
                                                        <CopyButton value={device.device_id} />
                                                    </td>
                                                    <td className="gateways-overview__mono">{device.reference || '—'}</td>
                                                    <td>{device.retailer_customer_id || '—'}</td>
                                                    <td>{device.count}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </AsyncSection>
                            </div>
                        ) : null}
                    </li>
                );
            })}
        </ul>
    );
};

export default GatewayAlarms;
